import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiPrinter, FiBook, FiEye, FiX, FiAlertCircle } from 'react-icons/fi';
import './KHS.css';

const KHS = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [semesters, setSemesters] = useState([]);
  const [khsList, setKhsList] = useState([]);
  const [studentData, setStudentData] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ipkData, setIpkData] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSemesterDetail, setSelectedSemesterDetail] = useState(null);
  const [detailKhsList, setDetailKhsList] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Print by year states
  const [showPrintByYear, setShowPrintByYear] = useState(false);
  const [printYear, setPrintYear] = useState('');
  const [printSemester, setPrintSemester] = useState('');
  const [printProdi, setPrintProdi] = useState('all');
  const [prodiList, setProdiList] = useState([]);

  // Fetch list of program studi on mount
  useEffect(() => {
    fetchProdiList();
  }, []);

  const fetchProdiList = async () => {
    try {
      const res = await axios.get('/api/students/stats');
      if (res.data.success && res.data.data.topStudentsByProgram) {
        const prodiNames = res.data.data.topStudentsByProgram.map(p => p._id).filter(Boolean);
        setProdiList(prodiNames);
      }
    } catch (error) {
      console.error('Error fetching prodi list:', error);
    }
  };

  // Search KHS
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Masukkan NIM mahasiswa');
      return;
    }

    if (!selectedSemester) {
      toast.warning('Pilih semester');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/khs/student/${searchQuery}/${selectedSemester}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setStudentData(response.data.data.student);
        setKhsList(response.data.data.khs);
        setStatistics(response.data.data.statistics);
        
        if (response.data.data.khs.length === 0) {
          toast.info('Tidak ada data KHS untuk semester ini');
        } else {
          toast.success(`Ditemukan ${response.data.data.khs.length} mata kuliah`);
        }
      }
    } catch (error) {
      console.error('Error searching KHS:', error);
      toast.error(error.response?.data?.message || 'Gagal mencari data KHS');
      setKhsList([]);
      setStudentData(null);
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  };

  // Load semesters when NIM is entered
  const handleNIMChange = async (nim) => {
    setSearchQuery(nim);
    
    if (nim.length >= 5) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/khs/semesters/${nim}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSemesters(response.data.data);
          
          // Load IPK
          const ipkResponse = await axios.get(`/api/khs/ipk/${nim}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (ipkResponse.data.success) {
            setIpkData(ipkResponse.data.data);
          }
        }
      } catch (error) {
        console.error('Error loading semesters:', error);
      }
    } else {
      setSemesters([]);
      setIpkData(null);
    }
  };

  // Print KHS
  const handlePrintKHS = () => {
    if (!searchQuery || !selectedSemester) {
      toast.warning('Pilih mahasiswa dan semester terlebih dahulu');
      return;
    }

    const printUrl = `http://localhost:5000/api/khs/print/${searchQuery}/${selectedSemester}`;
    window.open(printUrl, '_blank');
  };

  // Print KHS by year
  const handlePrintByYear = () => {
    if (!printYear || !printSemester) {
      toast.warning('Masukkan tahun pendaftaran dan semester');
      return;
    }

    // Open print window with prodi filter
    let printUrl = `http://localhost:5000/api/khs/print-by-year/${printYear}/${printSemester}`;
    if (printProdi && printProdi !== 'all') {
      printUrl += `?prodi=${encodeURIComponent(printProdi)}`;
    }
    window.open(printUrl, '_blank');
    toast.success('Membuka halaman cetak...');
  };

  // View semester detail
  const handleViewDetail = async (semester) => {
    setLoadingDetail(true);
    setShowDetailModal(true);
    setSelectedSemesterDetail(semester);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/khs/student/${searchQuery}/${semester.id_semester}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setDetailKhsList(response.data.data.khs);
      }
    } catch (error) {
      console.error('Error loading detail:', error);
      toast.error('Gagal memuat detail KHS');
      setDetailKhsList([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedSemesterDetail(null);
    setDetailKhsList([]);
  };

  // Get grade color
  const getGradeColor = (huruf) => {
    if (!huruf) return '';
    const grade = huruf[0];
    if (grade === 'A') return 'grade-a';
    if (grade === 'B') return 'grade-b';
    if (grade === 'C') return 'grade-c';
    if (grade === 'D') return 'grade-d';
    return 'grade-e';
  };

  // Format semester name
  const formatSemesterName = (semesterId) => {
    if (!semesterId) return '-';
    const year = semesterId.toString().substring(0, 4);
    const sem = semesterId.toString().substring(4);
    const nextYear = (parseInt(year) + 1).toString();
    const semName = sem === '1' ? 'Ganjil' : sem === '2' ? 'Genap' : 'Pendek';
    return `${year}/${nextYear} ${semName}`;
  };

  return (
    <div className="khs-container">
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiBook className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Kartu Hasil Studi (KHS)</h1>
              <p className="header-subtitle">Lihat dan kelola hasil studi mahasiswa per semester</p>
            </div>
          </div>
          <div className="header-actions">
            {ipkData && (
              <div className="ipk-badge">
                <div className="ipk-label">IPK</div>
                <div className="ipk-value">{ipkData.ipk}</div>
                <div className="ipk-detail">{ipkData.totalSKS} SKS</div>
              </div>
            )}
            <button 
              className="btn btn-glass btn-secondary" 
              onClick={() => setShowPrintByYear(!showPrintByYear)}
            >
              <FiPrinter /> <span className="btn-text">Cetak by Tahun</span>
            </button>
          </div>
        </div>
      </div>

      {/* Print by Year Panel */}
      {showPrintByYear && (
        <div className="card print-by-year-panel">
          <div className="card-header">
            <h3><FiPrinter /> Cetak KHS Berdasarkan Tahun Pendaftaran</h3>
            <button className="btn-close" onClick={() => setShowPrintByYear(false)}>
              <FiX />
            </button>
          </div>
          
          <div className="card-body">
            <div className="print-by-year-content">
              <div className="form-row">
                <div className="form-group">
                  <label>Tahun Pendaftaran</label>
                  <input
                    type="number"
                    value={printYear}
                    onChange={(e) => setPrintYear(e.target.value)}
                    placeholder="Contoh: 2024"
                    className="form-control"
                    min="2000"
                    max={new Date().getFullYear()}
                  />
                  <small className="text-muted">Masukkan tahun mahasiswa masuk (enrollment year)</small>
                </div>

                <div className="form-group">
                  <label>Semester</label>
                  <input
                    type="text"
                    value={printSemester}
                    onChange={(e) => setPrintSemester(e.target.value)}
                    placeholder="Contoh: 20251"
                    className="form-control"
                  />
                  <small className="text-muted">Format: YYYYS (20251 = 2025/2026 Ganjil)</small>
                </div>

                <div className="form-group">
                  <label>Program Studi</label>
                  <select
                    value={printProdi}
                    onChange={(e) => setPrintProdi(e.target.value)}
                    className="form-control"
                  >
                    <option value="all">Semua Program Studi</option>
                    {prodiList.map((prodi, index) => (
                      <option key={index} value={prodi}>{prodi}</option>
                    ))}
                  </select>
                  <small className="text-muted">Filter berdasarkan program studi</small>
                </div>

                <div className="form-group align-end">
                  <button 
                    className="btn btn-print btn-lg" 
                    onClick={handlePrintByYear}
                    disabled={!printYear || !printSemester}
                  >
                    <FiPrinter /> Cetak Semua KHS
                  </button>
                </div>
              </div>

              <div className="info-box">
                <FiAlertCircle />
                <div>
                  <strong>Informasi:</strong>
                  <p>Fitur ini akan mencetak KHS semua mahasiswa dari tahun pendaftaran yang dipilih untuk semester tertentu.</p>
                  <p>Setiap mahasiswa akan dicetak pada halaman terpisah dengan perhitungan IPS.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="card search-card">
        <div className="card-header">
          <h3>Cari KHS Mahasiswa</h3>
        </div>
        <div className="card-body">
          <div className="search-form">
            <div className="form-row">
              <div className="form-group">
                <label>NIM Mahasiswa</label>
                <div className="input-with-icon">
                  <FiSearch className="input-icon" />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Masukkan NIM..."
                    value={searchQuery}
                    onChange={(e) => handleNIMChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Semester</label>
                <select
                  className="form-control"
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  disabled={semesters.length === 0}
                >
                  <option value="">Pilih Semester</option>
                  {semesters.map((sem) => (
                    <option key={sem.id_semester} value={sem.id_semester}>
                      {formatSemesterName(sem.id_semester)} - {sem.totalMK} MK ({sem.totalSKS} SKS)
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group align-end">
                <button
                  className="btn btn-primary btn-search"
                  onClick={handleSearch}
                  disabled={loading || !searchQuery || !selectedSemester}
                >
                  {loading ? 'Mencari...' : 'Cari KHS'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Student Info */}
      {studentData && (
        <div className="card student-info-card">
          <div className="student-info-grid">
            <div className="info-item">
              <span className="info-label">NIM</span>
              <span className="info-value">{studentData.nim}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Nama</span>
              <span className="info-value">{studentData.name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Program Studi</span>
              <span className="info-value">{studentData.nama_program_studi}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Semester</span>
              <span className="info-value">{formatSemesterName(selectedSemester)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="stats-grid">
          <div className="stat-card stat-primary">
            <div className="stat-icon">
              <FiBook />
            </div>
            <div className="stat-details">
              <div className="stat-label">Total Mata Kuliah</div>
              <div className="stat-value">{statistics.totalMK}</div>
            </div>
          </div>

          <div className="stat-card stat-success">
            <div className="stat-icon">📚</div>
            <div className="stat-details">
              <div className="stat-label">Total SKS</div>
              <div className="stat-value">{statistics.totalSKS}</div>
            </div>
          </div>

          <div className="stat-card stat-info">
            <div className="stat-icon">📊</div>
            <div className="stat-details">
              <div className="stat-label">IPS Semester Ini</div>
              <div className="stat-value">{statistics.ips}</div>
            </div>
          </div>

          {ipkData && (
            <div className="stat-card stat-warning">
              <div className="stat-icon">🎓</div>
              <div className="stat-details">
                <div className="stat-label">IPK Kumulatif</div>
                <div className="stat-value">{ipkData.ipk}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Print Button */}
      {khsList.length > 0 && (
        <div className="print-section">
          <div className="print-info-box">
            <div className="print-info-content">
              <FiPrinter className="print-icon" />
              <div>
                <h4>Cetak KHS</h4>
                <p>Klik tombol di bawah untuk mencetak KHS mahasiswa</p>
              </div>
            </div>
            <button className="btn btn-print" onClick={handlePrintKHS}>
              <FiPrinter /> Cetak KHS
            </button>
          </div>
        </div>
      )}

      {/* KHS Table */}
      {khsList.length > 0 && (
        <div className="card table-card">
          <div className="card-header">
            <div>
              <h3>Kartu Hasil Studi (KHS)</h3>
              <p className="header-subtitle">Rincian nilai mata kuliah semester {formatSemesterName(selectedSemester)}</p>
            </div>
            <div className="card-actions">
              <span className="badge badge-info">{khsList.length} Mata Kuliah</span>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th width="50">No</th>
                  <th width="100">Kode MK</th>
                  <th>Mata Kuliah</th>
                  <th width="120">Kelas</th>
                  <th width="80" className="text-center">SKS</th>
                  <th width="100" className="text-center">Nilai Angka</th>
                  <th width="100" className="text-center">Nilai Huruf</th>
                  <th width="100" className="text-center">Mutu (Indeks)</th>
                  <th width="100" className="text-center">Mutu × SKS</th>
                </tr>
              </thead>
              <tbody>
                {khsList.map((khs, index) => (
                  <tr key={khs._id}>
                    <td className="text-center">{index + 1}</td>
                    <td><span className="badge badge-secondary">{khs.kode_mata_kuliah}</span></td>
                    <td>
                      <div className="course-info">
                        <strong className="course-name">{khs.nama_mata_kuliah}</strong>
                        {khs.id_semester && (
                          <small className="course-meta">Semester: {khs.id_semester}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      {khs.nama_kelas_kuliah ? (
                        <span className="badge badge-outline">{khs.nama_kelas_kuliah}</span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-center"><strong>{khs.sks_mata_kuliah}</strong></td>
                    <td className="text-center">
                      <span className="nilai-angka">
                        {khs.nilai_angka !== null ? khs.nilai_angka.toFixed(2) : '-'}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`grade-badge ${getGradeColor(khs.nilai_huruf)}`}>
                        {khs.nilai_huruf || '-'}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="nilai-indeks">
                        {khs.nilai_indeks !== null ? khs.nilai_indeks.toFixed(2) : '-'}
                      </span>
                    </td>
                    <td className="text-center">
                      <strong className="mutu-sks">
                        {khs.nilai_indeks !== null && khs.sks_mata_kuliah 
                          ? (khs.nilai_indeks * khs.sks_mata_kuliah).toFixed(2)
                          : '-'
                        }
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                  <td className="text-center"><strong>{statistics.totalSKS}</strong></td>
                  <td colSpan="3" className="text-right"><strong>IPS:</strong></td>
                  <td className="text-center"><strong>{statistics.ips}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Grade Distribution */}
          <div className="grade-distribution">
            <h4 className="distribution-title">Distribusi Nilai</h4>
            <div className="distribution-grid">
              {['A', 'B', 'C', 'D', 'E'].map(grade => {
                const count = khsList.filter(khs => khs.nilai_huruf === grade).length;
                const percentage = khsList.length > 0 ? ((count / khsList.length) * 100).toFixed(1) : 0;
                return (
                  <div key={grade} className="distribution-item">
                    <span className={`grade-badge ${getGradeColor(grade)}`}>{grade}</span>
                    <div className="distribution-info">
                      <span className="distribution-count">{count} MK</span>
                      <div className="distribution-bar">
                        <div 
                          className={`distribution-fill grade-${grade.toLowerCase()}`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="distribution-percentage">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Additional Stats */}
            <div className="khs-summary">
              <div className="summary-item">
                <span className="summary-label">Total Mutu × SKS:</span>
                <span className="summary-value">
                  {khsList.reduce((acc, khs) => {
                    return acc + (khs.nilai_indeks && khs.sks_mata_kuliah 
                      ? khs.nilai_indeks * khs.sks_mata_kuliah 
                      : 0);
                  }, 0).toFixed(2)}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Rata-rata Nilai:</span>
                <span className="summary-value">
                  {khsList.length > 0 
                    ? (khsList.reduce((acc, khs) => acc + (khs.nilai_angka || 0), 0) / khsList.length).toFixed(2)
                    : '0.00'
                  }
                </span>
              </div>
              <div className="summary-item highlight">
                <span className="summary-label">IPS Semester:</span>
                <span className="summary-value">{statistics.ips}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* IPK History */}
      {ipkData && ipkData.semesters && ipkData.semesters.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3>Riwayat Prestasi Akademik</h3>
            <p className="header-subtitle">Klik "Lihat Rincian" untuk melihat detail mata kuliah per semester</p>
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Semester</th>
                  <th className="text-center">Mata Kuliah</th>
                  <th className="text-center">SKS</th>
                  <th className="text-center">IPS</th>
                  <th className="text-center" width="150">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {ipkData.semesters.map((sem) => (
                  <tr key={sem.id_semester}>
                    <td>{formatSemesterName(sem.id_semester)}</td>
                    <td className="text-center">{sem.totalMK}</td>
                    <td className="text-center">{sem.totalSKS}</td>
                    <td className="text-center"><strong>{sem.ips}</strong></td>
                    <td className="text-center">
                      <button 
                        className="btn-detail"
                        onClick={() => handleViewDetail(sem)}
                        title="Lihat rincian nilai"
                      >
                        <FiEye /> Lihat Rincian
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td><strong>Total Kumulatif:</strong></td>
                  <td className="text-center"><strong>{ipkData.totalMK}</strong></td>
                  <td className="text-center"><strong>{ipkData.totalSKS}</strong></td>
                  <td className="text-center"><strong>IPK: {ipkData.ipk}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Rincian Nilai Semester</h3>
                {selectedSemesterDetail && (
                  <p className="modal-subtitle">
                    {formatSemesterName(selectedSemesterDetail.id_semester)} - 
                    IPS: <strong>{selectedSemesterDetail.ips}</strong>
                  </p>
                )}
              </div>
              <button className="modal-close" onClick={handleCloseModal}>
                <FiX size={24} />
              </button>
            </div>

            <div className="modal-body">
              {loadingDetail ? (
                <div className="modal-loading">
                  <div className="spinner"></div>
                  <p>Memuat data...</p>
                </div>
              ) : detailKhsList.length > 0 ? (
                <>
                  <div className="modal-stats">
                    <div className="modal-stat-item">
                      <span className="modal-stat-label">Total Mata Kuliah</span>
                      <span className="modal-stat-value">{detailKhsList.length}</span>
                    </div>
                    <div className="modal-stat-item">
                      <span className="modal-stat-label">Total SKS</span>
                      <span className="modal-stat-value">
                        {detailKhsList.reduce((acc, khs) => acc + (khs.sks_mata_kuliah || 0), 0)}
                      </span>
                    </div>
                    <div className="modal-stat-item highlight">
                      <span className="modal-stat-label">IPS</span>
                      <span className="modal-stat-value">{selectedSemesterDetail?.ips}</span>
                    </div>
                  </div>

                  <div className="modal-table-wrapper">
                    <table className="modal-table">
                      <thead>
                        <tr>
                          <th width="40">No</th>
                          <th width="100">Kode</th>
                          <th>Mata Kuliah</th>
                          <th width="60" className="text-center">SKS</th>
                          <th width="80" className="text-center">Nilai</th>
                          <th width="80" className="text-center">Huruf</th>
                          <th width="80" className="text-center">Mutu</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailKhsList.map((khs, index) => (
                          <tr key={khs._id}>
                            <td className="text-center">{index + 1}</td>
                            <td><span className="badge badge-secondary">{khs.kode_mata_kuliah}</span></td>
                            <td>
                              <strong className="course-name-modal">{khs.nama_mata_kuliah}</strong>
                              {khs.nama_kelas_kuliah && (
                                <small className="course-class"> - Kelas {khs.nama_kelas_kuliah}</small>
                              )}
                            </td>
                            <td className="text-center">{khs.sks_mata_kuliah}</td>
                            <td className="text-center">
                              <span className="nilai-angka-sm">
                                {khs.nilai_angka !== null ? khs.nilai_angka.toFixed(2) : '-'}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className={`grade-badge-sm ${getGradeColor(khs.nilai_huruf)}`}>
                                {khs.nilai_huruf || '-'}
                              </span>
                            </td>
                            <td className="text-center">
                              <strong>{khs.nilai_indeks !== null ? khs.nilai_indeks.toFixed(2) : '-'}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="modal-empty">
                  <FiBook size={48} />
                  <p>Tidak ada data nilai untuk semester ini</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Tutup
              </button>
              {detailKhsList.length > 0 && selectedSemesterDetail && (
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    const printUrl = `http://localhost:5000/api/khs/print/${searchQuery}/${selectedSemesterDetail.id_semester}`;
                    window.open(printUrl, '_blank');
                  }}
                >
                  <FiPrinter /> Cetak KHS
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!studentData && !loading && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>Cari KHS Mahasiswa</h3>
            <p>Masukkan NIM mahasiswa dan pilih semester untuk melihat hasil studi</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default KHS;
